import { acceptCompletion, autocompletion, completionKeymap, startCompletion } from '@codemirror/autocomplete';
import { Prec } from '@codemirror/state';
import { keymap, ViewPlugin } from '@codemirror/view';

import type { Completion, CompletionContext, CompletionResult } from '@codemirror/autocomplete';
import type { Extension } from '@codemirror/state';
import type { EditorView, ViewUpdate } from '@codemirror/view';

type CompletionSourceFn = (context: CompletionContext) => CompletionResult | null | Promise<CompletionResult | null>;

export type BracketConfig = {
  openPattern: RegExp;
  closingPattern: RegExp;
  triggerBeforePattern: RegExp;
  fromOffset: number;
  trimSearchText: boolean;
  lookaheadChars: number;
  makeInsertText: (value: string) => string;
};

export const DOUBLE_BRACE_CONFIG: BracketConfig = {
  openPattern: /\{\{[^}]*$/,
  closingPattern: /^\s*\}\}/,
  triggerBeforePattern: /\{\{\s*$/,
  fromOffset: 2,
  trimSearchText: true,
  lookaheadChars: 20,
  makeInsertText: (value) => ` ${value} }}`,
};

export const SINGLE_BRACKET_CONFIG: BracketConfig = {
  openPattern: /\[[^\]]*$/,
  closingPattern: /^\s*\]/,
  triggerBeforePattern: /\[\s*$/,
  fromOffset: 1,
  trimSearchText: false,
  lookaheadChars: 10,
  makeInsertText: (value) => `${value}]`,
};

// Returns an apply function that inserts the completion text and replaces any
// existing closing brackets to avoid duplication
export const createApplyFunction = (config: BracketConfig, value: string) => (view: EditorView, _completion: Completion, from: number) => {
  const cursorPos = view.state.selection.main.head;
  const afterCursor = view.state.sliceDoc(cursorPos, cursorPos + config.lookaheadChars);
  const closingMatch = afterCursor.match(config.closingPattern);
  const insertText = config.makeInsertText(value);
  const insertTo = closingMatch ? cursorPos + closingMatch[0].length : cursorPos;
  view.dispatch({
    changes: { from, to: insertTo, insert: insertText },
    selection: { anchor: from + insertText.length },
    userEvent: 'input.complete',
  });
};

// Exported for unit testing
export const createBracketSource =
  (config: BracketConfig, allFieldOptions: Completion[]) =>
  (context: CompletionContext): CompletionResult | null => {
    const bracketMatch = context.matchBefore(config.openPattern);

    if (!bracketMatch) {
      return null;
    }

    const rawText = bracketMatch.text.slice(config.fromOffset);
    const searchText = (config.trimSearchText ? rawText.trim() : rawText).toLowerCase();

    const filteredOptions =
      searchText === '' ? allFieldOptions : allFieldOptions.filter((opt) => opt.label.toLowerCase().includes(searchText));

    if (filteredOptions.length === 0) {
      return null;
    }
    return {
      from: bracketMatch.from + config.fromOffset,
      options: filteredOptions,
      filter: false,
    };
  };

export const createCursorTriggerPlugin = (config: BracketConfig) =>
  ViewPlugin.fromClass(
    class {
      update(update: ViewUpdate) {
        const { state } = update;
        const cursorPos = state.selection.main.head;
        const beforeCursor = state.sliceDoc(Math.max(0, cursorPos - config.lookaheadChars), cursorPos);

        // On deletion inside brackets, re-trigger completion so the dropdown updates
        if (update.docChanged && update.transactions.some((t) => t.isUserEvent('delete'))) {
          if (config.openPattern.test(beforeCursor)) {
            const { view } = update;
            Promise.resolve().then(() => startCompletion(view));
          }
          return;
        }

        // On cursor placement inside empty brackets (no doc change, just selection move)
        if (update.selectionSet && !update.docChanged) {
          const afterCursor = state.sliceDoc(cursorPos, cursorPos + config.lookaheadChars);
          if (config.triggerBeforePattern.test(beforeCursor) && config.closingPattern.test(afterCursor)) {
            const { view } = update;
            Promise.resolve().then(() => startCompletion(view));
          }
        }
      }
    },
  );

export const createBracketAutocompleteExtensions = (
  config: BracketConfig,
  allFieldOptions: Completion[],
  additionalSources: CompletionSourceFn[] = [],
): Extension[] => {
  const bracketSource = createBracketSource(config, allFieldOptions);
  const cursorTriggerPlugin = createCursorTriggerPlugin(config);

  return [
    autocompletion({
      override: [bracketSource, ...additionalSources],
      activateOnTyping: true,
      maxRenderedOptions: 15,
    }),
    keymap.of(completionKeymap),
    // Tab accepts the selected completion when the menu is open; falls through otherwise
    Prec.highest(keymap.of([{ key: 'Tab', run: acceptCompletion }])),
    cursorTriggerPlugin,
  ];
};
